import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';

const navLinks = [
  { href: '/', label: 'Start' },
  { href: '/sportangebote', label: 'Sportangebote' },
  { href: '/vereinsleben', label: 'Vereinsleben' },
  { href: '/ueber-uns', label: 'Über uns' },
  { href: '/kontakt', label: 'Kontakt' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Fixed: handleScroll is now defined inside useEffect

  const isActive = (href) => location.pathname === href;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.08)]'
          : 'bg-white/90 backdrop-blur-sm'
      } border-b border-black/[0.08]`}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/images/verein/Logo.jpg" 
              alt="HSC Alswede Logo" 
              className="w-9 h-9 object-contain rounded-xl"
            />
            <div className="hidden sm:block">
              <div className="font-semibold text-[#1a1a1a] text-sm leading-tight font-['Space_Grotesk']">
                HSC Alswede
              </div>
              <div className="text-xs text-gray-500">von 1946 e.V.</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Hauptnavigation">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive(link.href)
                    ? 'text-[#1a1a1a] active'
                    : 'text-gray-600 hover:text-[#1a1a1a]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/kontakt">
              <Button
                data-testid="header-join-button"
                className="h-10 px-5 bg-[#F5C200] text-[#1a1a1a] hover:bg-[#E6B400] font-semibold rounded-xl border-0 shadow-none transition-colors"
              >
                Mitglied werden
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-nav-open-button"
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Menü öffnen"
              >
                <Menu className="w-5 h-5 text-[#1a1a1a]" />
              </button>
            </SheetTrigger>
            <SheetContent
              data-testid="mobile-nav-sheet"
              side="right"
              className="w-80 bg-white p-0"
            >
              <div className="flex flex-col h-full">
                {/* Sheet Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#F5C200] rounded-xl flex items-center justify-center font-bold text-[#1a1a1a] text-sm">
                      HSC
                    </div>
                    <div>
                      <div className="font-semibold text-[#1a1a1a] text-sm">HSC Alswede</div>
                      <div className="text-xs text-gray-500">von 1946 e.V.</div>
                    </div>
                  </div>
                </div>

                {/* Sheet Links */}
                <nav className="flex-1 p-5 space-y-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      data-testid="mobile-nav-link"
                      onClick={() => setOpen(false)}
                      className={`flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                        isActive(link.href)
                          ? 'bg-[#F5C200]/15 text-[#1a1a1a]'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-[#1a1a1a]'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>

                <Separator />

                {/* Sheet Footer */}
                <div className="p-5 space-y-3">
                  <Link to="/kontakt" onClick={() => setOpen(false)}>
                    <Button className="w-full h-12 bg-[#F5C200] text-[#1a1a1a] hover:bg-[#E6B400] font-semibold rounded-xl border-0 shadow-none transition-colors">
                      Jetzt Mitglied werden
                    </Button>
                  </Link>
                  <div className="space-y-1.5 text-xs text-gray-500 text-center">
                    <div>📍 Schanzeweg 7, 32312 Lübbecke</div>
                    <div>✉️ patrick.bruels@hsc-alswede.de</div>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
